import { baseApi } from "@/api/baseApi";
import { RENEWAL_TAGS } from "@/api/renewals.constraints";
import { SUPPLIER_TAGS } from "@/api/suppliers.constraints";
import type {
  LegalNote,
  LegalReview,
  LegalReviewUpdate,
  RenewalFamilyDetail,
  RenewalFamilyList,
} from "@/types/renewals";

const BASE = "/pf-mainweb/suppliers/renewals/families";

// Both views depend on supplier metadata/status and Contract Family membership.
const supplierListTag = { type: SUPPLIER_TAGS.SUPPLIERS, id: "LIST" } as const;

export const renewalsApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getRenewalFamilies: builder.query<RenewalFamilyList, void>({
      query: () => ({ url: BASE, method: "GET" }),
      providesTags: [RENEWAL_TAGS.FAMILIES, supplierListTag],
    }),
    getRenewalFamily: builder.query<RenewalFamilyDetail, string>({
      query: relationId => ({ url: `${BASE}/${encodeURIComponent(relationId)}`, method: "GET" }),
      providesTags: (_result, _error, relationId) => [{ type: RENEWAL_TAGS.FAMILY, id: relationId }, supplierListTag],
    }),
    updateLegalReview: builder.mutation<LegalReview, { relationId: string; data: LegalReviewUpdate }>({
      query: ({ relationId, data }) => ({
        url: `${BASE}/${encodeURIComponent(relationId)}/legal-review`,
        method: "PUT",
        data,
      }),
      // The review belongs to the folder, so every family card of that folder changes.
      invalidatesTags: [RENEWAL_TAGS.FAMILIES, RENEWAL_TAGS.FAMILY],
    }),
    addLegalNote: builder.mutation<LegalNote, { relationId: string; body: string }>({
      query: ({ relationId, body }) => ({
        url: `${BASE}/${encodeURIComponent(relationId)}/legal-notes`,
        method: "POST",
        data: { body },
      }),
      invalidatesTags: [RENEWAL_TAGS.FAMILY],
    }),
  }),
});

export const {
  useGetRenewalFamiliesQuery,
  useGetRenewalFamilyQuery,
  useUpdateLegalReviewMutation,
  useAddLegalNoteMutation,
} = renewalsApi;
